import uPlot, { AlignedData } from 'uplot';
import { addOKLCHOpacity, getChartDimensions, setupChartObservers } from './lib/plot-utils';
import { erf } from './lib/erf';

function sigmoid(x: number): number {
  const ex = Math.exp(x);
  return ex / (1 + ex);
}

function d_sigmoid(x: number): number {
  const enx = Math.exp(-x);
  return enx / (1 + enx) ** 2;
}

function tanh(x: number): number {
  const e2x = Math.exp(2 * x);
  return (e2x - 1) / (e2x + 1);
}

function d_tanh(x: number): number {
  const thx = tanh(x);
  return 1 - thx * thx;
}

function softplus(x: number, beta: number = 1.0): number {
  return 1.0 / beta * Math.log(1 + Math.exp(beta * x));
}

function d_softplus(x: number, beta: number = 1.0): number {
  return sigmoid(beta * x);
}

function ReLU(x: number): number {
  return Math.max(0, x);
}

function d_ReLU(x: number): number {
  return x <= 0 ? 0 : 1;
}

function leaky_ReLU(x: number): number {
  return x <= 0 ? 0.01 * x : x;
}

function d_leaky_ReLU(x: number): number {
  return x <= 0 ? 0.01 : 1;
}

function PReLU(x: number): number {
  // PReLU: alpha is a learnable parameter
  const alpha = 0.25;
  return x <= 0 ? alpha * x : x;
}

function d_PReLU(x: number): number {
  // PReLU: alpha is a learnable parameter
  const alpha = 0.25;
  return x <= 0 ? alpha : 1;
}

function ELU(x: number): number {
  // ELU: alpha is a fixed hyperparameter
  const alpha = 1.0;
  return x <= 0 ? alpha * (Math.exp(x) - 1) : x;
}

function d_ELU(x: number): number {
  // ELU: alpha is a fixed hyperparameter
  const alpha = 1.0;
  return x <= 0 ? alpha * Math.exp(x) : 1;
}

/**
 * CDF of the normal (Gaussian) distribution.
 */
function Phi(x: number, mu: number = 0.0, sigma: number = 1.0): number {
  return 0.5 * (1 + erf((x - mu) / (sigma * Math.sqrt(2))))
}

/**
 * PDF of the normal (Gaussian) distribution.
 */
function phi(x: number, mu: number = 0.0, sigma: number = 1.0): number {
  const ssq = sigma * sigma;
  const t1 = 1.0 / Math.sqrt(2 * Math.PI * ssq);
  const t2 = Math.exp(-1 * (x - mu) ** 2 / (2 * ssq));
  return t1 * t2;
}

function GELU(x: number): number {
  return x * Phi(x);
}

function d_GELU(x: number): number {
  return Phi(x) + x * phi(x);
}

function swish(x: number): number {
  return x * sigmoid(x);
}

function d_swish(x: number): number {
  return sigmoid(x) + x * d_sigmoid(x);
}

// GLU parameters (manually sync defaults; read from sliders)
let gluParams = { w: 1.6, b: 1.4, v: -0.3, c: 1.2 };

function onGLUParamsChange(callback: () => void) {
  const sliders = ['glu-w', 'glu-b', 'glu-v', 'glu-c'] as const;
  const paramKeys = ['w', 'b', 'v', 'c'] as const;

  sliders.forEach((id, i) => {
    const slider = document.getElementById(id) as HTMLInputElement | null;
    const valueSpan = document.getElementById(`${id}-value`);
    slider?.addEventListener('input', () => {
      const val = parseFloat(slider.value);
      gluParams[paramKeys[i]] = val;
      if (valueSpan) valueSpan.textContent = val.toFixed(1);
      callback();
    });
  });
}

function GLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return sigmoid(x * w + b) * (x * v + c);
}

function d_GLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return d_sigmoid(x * w + b) * w * (x * v + c) + sigmoid(x * w + b) * v;
}

function bilinear(x: number): number {
  const { w, b, v, c } = gluParams;
  return (x * w + b) * (x * v + c);
}

function d_bilinear(x: number): number {
  const { w, b, v, c } = gluParams;
  return w * (x * v + c) + (x * w + b) * v;
}

function SwiGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return swish(x * w + b) * (x * v + c);
}

function d_SwiGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return d_swish(x * w + b) * w * (x * v + c) + swish(x * w + b) * v;
}

function GEGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return GELU(x * w + b) * (x * v + c);
}

function d_GEGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return d_GELU(x * w + b) * w * (x * v + c) + GELU(x * w + b) * v;
}

function ReGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return ReLU(x * w + b) * (x * v + c);
}

function d_ReGLU(x: number): number {
  const { w, b, v, c } = gluParams;
  return d_ReLU(x * w + b) * w * (x * v + c) + ReLU(x * w + b) * v;
}



function initActivationChart(params: {
  id: string,
  title: string;
  f: (x: number) => number;
  color: string;
  Y_MIN?: number;
  Y_MAX?: number;
  X_MIN?: number;
  X_MAX?: number;
  SAMPLE_STEP?: number;
  MIN_HEIGHT?: number;
  ASPECT_RATIO?: number;
  onExternalChange?: (callback: () => void) => void;
}) {
  const {
    id,
    title,
    f,
    color,
    Y_MIN,
    Y_MAX,
    X_MIN = -5,
    X_MAX = 5,
    SAMPLE_STEP = 0.01,
    MIN_HEIGHT = 175,
    ASPECT_RATIO = 16 / 9,
    onExternalChange,
  } = params;

  function generateChartData(): AlignedData {
    const xValues: number[] = [];
    const yValues: number[] = [];
    for (let x = X_MIN; x <= X_MAX; x += SAMPLE_STEP) {
      xValues.push(x);
      const y = f(x);
      yValues.push(Number.isFinite(y) ? y : 0);
    }
    return [xValues, yValues]
  }

  let data = generateChartData();

  function createChartOptions(width: number, height: number): uPlot.Options {
    const docStyle = window.getComputedStyle(document.documentElement);
    const textColor = docStyle.getPropertyValue('--chart-text').trim();
    const gridColor = docStyle.getPropertyValue('--chart-grid').trim();
    const lineColor = docStyle.getPropertyValue(color).trim();
    const fillColor = addOKLCHOpacity(lineColor, 0.1);

    return {
      title: title,
      width,
      height,
      scales: {
        x: {
          time: false,
        },
        y: {
          range: (_uPlot, dataMin, dataMax) => {
            let min = Y_MIN ?? dataMin;
            let max = Y_MAX ?? dataMax;
            // Prevent zero-range scale that crashes uPlot's axis splitter
            if (max - min < 0.1) {
              const mid = (max + min) / 2;
              min = mid - 0.5;
              max = mid + 0.5;
            }
            return [min, max];
            // return [Y_MIN ?? dataMin, Y_MAX ?? dataMax];
          }
        }
      },
      series: [
        {},
        {
          label: title,
          stroke: lineColor,
          fill: fillColor,
          width: 2,
          value: (u, v, seriesIdx, idx) => { return idx == null ? '' : v.toFixed(2) },
        },
      ],
      axes: [
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
        {
          stroke: textColor,
          grid: {
            stroke: gridColor,
          },
        },
      ],
    };
  }

  const container = document.getElementById(id);
  if (!container) {
    console.error(`Chart container #${id} not found`);
    return;
  }
  const { width, height } = getChartDimensions(container, MIN_HEIGHT, ASPECT_RATIO);
  const opts = createChartOptions(width, height);
  const chartRef = { current: new uPlot(opts, data, container) as uPlot | null };
  setupChartObservers(
    container,
    chartRef,
    MIN_HEIGHT,
    ASPECT_RATIO,
    createChartOptions,
    () => generateChartData(),
    (_: uPlot) => { },
    onExternalChange,
  );
}

// main: init all the charts
initActivationChart({ id: "sigmoid-chart", title: "Sigmoid", f: sigmoid, color: "--chart-color-blue", Y_MIN: -0.1, Y_MAX: 1.1 });
initActivationChart({ id: "d-sigmoid-chart", title: "d/dx Sigmoid", f: d_sigmoid, color: "--chart-color-blue", Y_MIN: -0.1, Y_MAX: 1.1 });
initActivationChart({ id: "tanh-chart", title: "tanh", f: tanh, color: "--chart-color-red", Y_MIN: -1, Y_MAX: 1 });
initActivationChart({ id: "d-tanh-chart", title: "d/dx tanh", f: d_tanh, color: "--chart-color-red", Y_MIN: -1, Y_MAX: 1 });
initActivationChart({ id: "softplus-chart", title: "Softplus", f: softplus, color: "--chart-color-orange", Y_MIN: -0.5 });
initActivationChart({ id: "d-softplus-chart", title: "d/dx Softplus", f: d_softplus, color: "--chart-color-orange", Y_MIN: -0.1, Y_MAX: 1.1 });
initActivationChart({ id: "relu-chart", title: "ReLU", f: ReLU, color: "--chart-color-green", Y_MIN: -0.5 });
initActivationChart({ id: "d-relu-chart", title: "d/dx ReLU", f: d_ReLU, color: "--chart-color-green", Y_MIN: -0.25 });
initActivationChart({ id: "leaky-relu-chart", title: "Leaky ReLU", f: leaky_ReLU, color: "--chart-color-green", Y_MIN: -0.5 });
initActivationChart({ id: "d-leaky-relu-chart", title: "d/dx Leaky ReLU", f: d_leaky_ReLU, color: "--chart-color-green", Y_MIN: -0.25 });
initActivationChart({ id: "prelu-chart", title: "PReLU", f: PReLU, color: "--chart-color-green", Y_MIN: -2 });
initActivationChart({ id: "d-prelu-chart", title: "d/dx PReLU", f: d_PReLU, color: "--chart-color-green", Y_MIN: -0.25 });
initActivationChart({ id: "elu-chart", title: "ELU", f: ELU, color: "--chart-color-teal", Y_MIN: -2 });
initActivationChart({ id: "d-elu-chart", title: "d/dx ELU", f: d_ELU, color: "--chart-color-teal", Y_MIN: -0.25 });
initActivationChart({ id: "gelu-chart", title: "GELU", f: GELU, color: "--chart-color-purple", Y_MIN: -0.25 });
initActivationChart({ id: "d-gelu-chart", title: "d/dx GELU", f: d_GELU, color: "--chart-color-purple", Y_MIN: -0.25 });
initActivationChart({ id: "swish-chart", title: "Swish", f: swish, color: "--chart-color-yellow", Y_MIN: -0.25 });
initActivationChart({ id: "d-swish-chart", title: "d/dx Swish", f: d_swish, color: "--chart-color-yellow", Y_MIN: -0.25 });
initActivationChart({ id: "glu-chart", title: "GLU", f: GLU, color: "--chart-color-blue", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "d-glu-chart", title: "d/dx GLU", f: d_GLU, color: "--chart-color-blue", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "bilinear-chart", title: "Bilinear", f: bilinear, color: "--chart-color-pink", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "d-bilinear-chart", title: "d/dx Bilinear", f: d_bilinear, color: "--chart-color-pink", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "reglu-chart", title: "ReGLU", f: ReGLU, color: "--chart-color-green", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "d-reglu-chart", title: "d/dx ReGLU", f: d_ReGLU, color: "--chart-color-green", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "geglu-chart", title: "GEGLU", f: GEGLU, color: "--chart-color-purple", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "d-geglu-chart", title: "d/dx GEGLU", f: d_GEGLU, color: "--chart-color-purple", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "swiglu-chart", title: "SwiGLU", f: SwiGLU, color: "--chart-color-yellow", onExternalChange: onGLUParamsChange });
initActivationChart({ id: "d-swiglu-chart", title: "d/dx SwiGLU", f: d_SwiGLU, color: "--chart-color-yellow", onExternalChange: onGLUParamsChange });
